<template>
	<div class="lesson-form">
		<!-- Header -->
		<div class="lesson-form__header">
			<h3 class="lesson-form__title">
				{{ isEdit ? 'Редактирование урока' : 'Создание урока' }}
			</h3>
			<button
				class="lesson-form__close-btn"
				type="button"
				@click="handleCancel"
				title="Закрыть"
			>
				✕
			</button>
		</div>

		<!-- Form -->
		<form class="lesson-form__form" @submit.prevent="handleSubmit">
			<!-- Lesson Type -->
			<div class="lesson-form__field">
				<label class="lesson-form__label">
					Тип урока <span class="required">*</span>
				</label>
				<div class="lesson-form__radio-group">
					<label class="lesson-form__radio-label">
						<input
							v-model="formData.lesson_type"
							type="radio"
							value="video"
							class="lesson-form__radio"
						/>
						<span>🎥 Видео урок</span>
					</label>
					<label class="lesson-form__radio-label">
						<input
							v-model="formData.lesson_type"
							type="radio"
							value="article"
							class="lesson-form__radio"
						/>
						<span>📄 Статья</span>
					</label>
				</div>
			</div>

			<!-- Title -->
			<div class="lesson-form__field">
				<label class="lesson-form__label" for="lesson-title">
					Название урока <span class="required">*</span>
				</label>
				<input
					id="lesson-title"
					v-model="formData.title"
					class="lesson-form__input"
					type="text"
					placeholder="Введите название урока"
					required
				/>
			</div>

			<!-- Description -->
			<div class="lesson-form__field">
				<label class="lesson-form__label" for="lesson-description">
					Описание
				</label>
				<textarea
					id="lesson-description"
					v-model="formData.description"
					class="lesson-form__textarea"
					placeholder="Опишите содержание урока"
					rows="3"
				></textarea>
			</div>

			<!-- Content (Article) -->
			<div v-if="formData.lesson_type === 'article'" class="lesson-form__field">
				<label class="lesson-form__label" for="lesson-content">
					Содержание статьи <span class="required">*</span>
				</label>
				<textarea
					id="lesson-content"
					v-model="formData.content"
					class="lesson-form__textarea lesson-form__textarea--large"
					placeholder="Введите текст статьи"
					rows="10"
					required
				></textarea>
			</div>

			<!-- Video URL (Video) -->
			<div v-if="formData.lesson_type === 'video'" class="lesson-form__field">
				<label class="lesson-form__label" for="lesson-video">
					Ссылка на видео или загрузка
				</label>
				<input
					id="lesson-video"
					v-model="formData.video_url"
					class="lesson-form__input"
					type="url"
					placeholder="https://youtube.com/watch?v=..."
				/>
				<p class="lesson-form__hint">
					Или загрузите видео файл (макс 500MB)
				</p>
				<div class="lesson-form__upload">
					<button
						class="lesson-form__upload-btn"
						type="button"
						@click="selectVideo"
					>
						<span class="lesson-form__upload-icon">🎥</span>
						{{ videoFile ? videoFile.name : 'Выбрать видео файл' }}
					</button>
					<input
						ref="videoInput"
						type="file"
						accept="video/*"
						style="display: none"
						@change="handleVideoChange"
					/>
				</div>
			</div>

			<!-- Duration -->
			<div class="lesson-form__field">
				<label class="lesson-form__label" for="lesson-duration">
					Длительность (минуты)
				</label>
				<input
					id="lesson-duration"
					v-model.number="formData.duration_minutes"
					class="lesson-form__input"
					type="number"
					min="0"
					placeholder="Примерная длительность урока"
				/>
			</div>

			<!-- Materials -->
			<div class="lesson-form__field">
				<label class="lesson-form__label">Дополнительные материалы</label>
				<div class="lesson-form__upload">
					<button
						class="lesson-form__upload-btn"
						type="button"
						@click="selectMaterials"
					>
						<span class="lesson-form__upload-icon">📎</span>
						Добавить материалы
					</button>
					<input
						ref="materialsInput"
						type="file"
						multiple
						style="display: none"
						@change="handleMaterialsChange"
					/>
				</div>
				<div
					v-if="materialFiles.length > 0"
					class="lesson-form__materials-list"
				>
					<div
						v-for="(file, index) in materialFiles"
						:key="index"
						class="lesson-form__material-item"
					>
						<span class="lesson-form__material-name">{{ file.name }}</span>
						<button
							class="lesson-form__material-remove"
							type="button"
							@click="removeMaterial(index)"
							title="Удалить"
						>
							✕
						</button>
					</div>
				</div>
			</div>

			<!-- Actions -->
			<div class="lesson-form__actions">
				<button
					class="lesson-form__btn lesson-form__btn--cancel"
					type="button"
					@click="handleCancel"
					:disabled="saving"
				>
					Отменить
				</button>
				<button
					class="lesson-form__btn lesson-form__btn--submit"
					type="submit"
					:disabled="saving"
				>
					{{ saving ? 'Сохранение...' : isEdit ? 'Обновить' : 'Создать' }}
				</button>
			</div>
		</form>
	</div>
</template>

<script>
export default {
	name: 'LessonForm',
	props: {
		lesson: {
			type: Object,
			default: null,
		},
		saving: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			formData: {
				lesson_type: 'video',
				title: '',
				description: '',
				content: '',
				video_url: '',
				duration_minutes: null,
			},
			videoFile: null,
			materialFiles: [],
		}
	},
	computed: {
		isEdit() {
			return !!this.lesson
		},
	},
	mounted() {
		if (this.lesson) {
			this.formData = {
				lesson_type: this.lesson.lesson_type || 'video',
				title: this.lesson.title || '',
				description: this.lesson.description || '',
				content: this.lesson.content || '',
				video_url: this.lesson.video_url || '',
				duration_minutes: this.lesson.duration_minutes || null,
			}
		}
	},
	methods: {
		selectVideo() {
			this.$refs.videoInput.click()
		},

		handleVideoChange(event) {
			const file = event.target.files[0]
			if (!file) return

			// Проверка размера (макс 500MB)
			if (file.size > 500 * 1024 * 1024) {
				alert('Размер видео не должен превышать 500MB')
				return
			}

			// Проверка типа
			if (!file.type.startsWith('video/')) {
				alert('Пожалуйста, выберите видео файл')
				return
			}

			this.videoFile = file
		},

		selectMaterials() {
			this.$refs.materialsInput.click()
		},

		handleMaterialsChange(event) {
			const files = Array.from(event.target.files)
			this.materialFiles.push(...files)
		},

		removeMaterial(index) {
			this.materialFiles.splice(index, 1)
		},

		handleSubmit() {
			if (!this.formData.title.trim()) {
				alert('Пожалуйста, введите название урока')
				return
			}

			if (
				this.formData.lesson_type === 'article' &&
				!this.formData.content.trim()
			) {
				alert('Пожалуйста, введите содержание статьи')
				return
			}

			const data = {
				...this.formData,
				videoFile: this.videoFile,
				materialFiles: this.materialFiles,
			}

			this.$emit('submit', data)
		},

		handleCancel() {
			if (
				confirm(
					'Вы уверены, что хотите отменить? Все несохраненные данные будут потеряны.'
				)
			) {
				this.$emit('cancel')
			}
		},
	},
}
</script>

<style scoped>
.lesson-form {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 24px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	max-height: 80vh;
	overflow-y: auto;
}

/* Header */
.lesson-form__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
	padding-bottom: 16px;
	border-bottom: 2px solid #e8e8e8;
}

.lesson-form__title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 22px;
	font-weight: 600;
	margin: 0;
}

.lesson-form__close-btn {
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: transparent;
	border: none;
	border-radius: 50%;
	color: #636363;
	font-size: 24px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.lesson-form__close-btn:hover {
	background-color: #f5f5f5;
	color: #dc3545;
}

/* Form */
.lesson-form__form {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.lesson-form__field {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.lesson-form__label {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
}

.required {
	color: #dc3545;
}

.lesson-form__input,
.lesson-form__textarea {
	padding: 12px 16px;
	border: 2px solid #e8e8e8;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	color: #252641;
	transition: all 0.2s ease;
}

.lesson-form__input:focus,
.lesson-form__textarea:focus {
	outline: none;
	border-color: #0095ec;
	box-shadow: 0 0 0 3px rgba(0, 149, 236, 0.1);
}

.lesson-form__textarea {
	resize: vertical;
	min-height: 80px;
}

.lesson-form__textarea--large {
	min-height: 200px;
}

.lesson-form__hint {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 12px;
	margin: 0;
}

/* Radio Group */
.lesson-form__radio-group {
	display: flex;
	gap: 16px;
}

.lesson-form__radio-label {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 20px;
	border: 2px solid #e8e8e8;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.2s ease;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	color: #636363;
}

.lesson-form__radio-label:has(.lesson-form__radio:checked) {
	border-color: #0095ec;
	background-color: #f0f8ff;
	color: #0095ec;
}

.lesson-form__radio {
	width: 18px;
	height: 18px;
	cursor: pointer;
}

/* Upload */
.lesson-form__upload {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.lesson-form__upload-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 12px 20px;
	border: 2px dashed #e8e8e8;
	border-radius: 8px;
	background-color: #f8f8f8;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
}

.lesson-form__upload-btn:hover {
	border-color: #0095ec;
	background-color: #f0f8ff;
	color: #0095ec;
}

.lesson-form__upload-icon {
	font-size: 20px;
}

/* Materials List */
.lesson-form__materials-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-top: 8px;
}

.lesson-form__material-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 12px;
	background-color: #f8f8f8;
	border-radius: 6px;
}

.lesson-form__material-name {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
}

.lesson-form__material-remove {
	width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: transparent;
	border: none;
	border-radius: 50%;
	color: #dc3545;
	font-size: 16px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.lesson-form__material-remove:hover {
	background-color: #fff5f5;
}

/* Actions */
.lesson-form__actions {
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	margin-top: 8px;
	padding-top: 20px;
	border-top: 2px solid #e8e8e8;
}

.lesson-form__btn {
	padding: 12px 24px;
	border: none;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
}

.lesson-form__btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.lesson-form__btn--cancel {
	background-color: #f5f5f5;
	color: #636363;
}

.lesson-form__btn--cancel:hover:not(:disabled) {
	background-color: #e8e8e8;
}

.lesson-form__btn--submit {
	background-color: #0095ec;
	color: #ffffff;
}

.lesson-form__btn--submit:hover:not(:disabled) {
	background-color: #0077be;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.3);
}
</style>

